<template>
  <el-dialog
    :title="dialogTitle"
    :visible.sync="dialogVisible"
    width="600px"
    :close-on-click-modal="false"
    @close="handleClose"
  >
    <div class="invoice-dialog">
      <!-- 已有发票记录 - 显示详情 -->
      <div v-if="invoiceRecord && invoiceRecord.id" class="invoice-detail">
        <div class="detail-item">
          <span class="detail-label">发票类型：</span>
          <span class="detail-value">{{ invoiceRecord.type === 0 ? '增值税普通发票' : '增值税专用发票' }}</span>
        </div>
        <div class="detail-item">
          <span class="detail-label">单位名称：</span>
          <span class="detail-value">{{ invoiceRecord.buyer?.name || '-' }}</span>
        </div>
        <div class="detail-item">
          <span class="detail-label">纳税人识别号：</span>
          <span class="detail-value">{{ invoiceRecord.buyer?.taxid || '-' }}</span>
        </div>
        <div class="detail-item">
          <span class="detail-label">电子邮箱：</span>
          <span class="detail-value">{{ invoiceRecord.recipient?.email || '-' }}</span>
        </div>
        <div v-if="invoiceRecord.type === 1" class="detail-item">
          <span class="detail-label">地址：</span>
          <span class="detail-value">{{ invoiceRecord.buyer?.address || '-' }}</span>
        </div>
        <div v-if="invoiceRecord.type === 1" class="detail-item">
          <span class="detail-label">电话：</span>
          <span class="detail-value">{{ invoiceRecord.buyer?.phone || '-' }}</span>
        </div>
        <div v-if="invoiceRecord.type === 1" class="detail-item">
          <span class="detail-label">开户行：</span>
          <span class="detail-value">{{ invoiceRecord.buyer?.bank || '-' }}</span>
        </div>
        <div v-if="invoiceRecord.type === 1" class="detail-item">
          <span class="detail-label">银行账号：</span>
          <span class="detail-value">{{ invoiceRecord.buyer?.bankno || '-' }}</span>
        </div>
        <div class="detail-item">
          <span class="detail-label">开票金额：</span>
          <span class="detail-value">¥{{ (invoiceRecord.amount || 0).toFixed(2) }}</span>
        </div>
        <div class="detail-item">
          <span class="detail-label">开票项目：</span>
          <span class="detail-value">{{ invoiceRecord.details?.[0]?.goodsName || '-' }}</span>
        </div>
        <div v-if="invoiceRecord.userremark" class="detail-item">
          <span class="detail-label">备注：</span>
          <span class="detail-value">{{ invoiceRecord.userremark }}</span>
        </div>
        <div v-if="invoiceRecord.status" class="detail-item">
          <span class="detail-label">状态：</span>
          <el-tag :type="getStatusType(invoiceRecord.status)" size="small">
            {{ getStatusText(invoiceRecord.status) }}
          </el-tag>
        </div>
        <div v-if="invoiceRecord.pdfurl" class="detail-item">
          <span class="detail-label">发票PDF：</span>
          <el-button type="text" size="small" @click="handleCopyPdfUrl">复制PDF地址</el-button>
        </div>
        <div v-if="invoiceRecord.imgurl" class="detail-item">
          <span class="detail-label">发票预览：</span>
          <el-image 
            :src="invoiceRecord.imgurl" 
            :preview-src-list="[invoiceRecord.imgurl]"
            fit="cover"
            style="width: 100px; height: 100px; border-radius: 4px; cursor: pointer;"
          />
        </div>
      </div>

      <!-- 无发票记录 - 显示表单 -->
      <el-form
        v-else
        ref="invoiceFormRef"
        :model="invoiceForm"
        :rules="rules"
        label-width="120px"
      >
        <!-- 发票类型 -->
        <el-form-item label="发票类型" prop="type" required>
          <el-radio-group v-model="invoiceForm.type">
            <el-radio label="0">增值税普通发票</el-radio>
            <el-radio label="1">增值税专用发票</el-radio>
          </el-radio-group>
        </el-form-item>

        <!-- 单位名称 -->
        <el-form-item label="单位名称" prop="buyername" required>
          <el-input
            v-model="invoiceForm.buyername"
            placeholder="请输入单位名称"
            @input="onBuyernameInput"
          />
        </el-form-item>

        <!-- 税号 -->
        <el-form-item label="纳税人识别号" prop="buyertaxid" required>
          <el-input
            v-model="invoiceForm.buyertaxid"
            placeholder="请输入纳税人识别号"
          />
        </el-form-item>

        <!-- 电子邮箱 -->
        <el-form-item label="电子邮箱" prop="recipientemail" required>
          <el-input
            v-model="invoiceForm.recipientemail"
            placeholder="请输入收票人电子邮箱"
          />
        </el-form-item>

        <!-- 专票额外字段 - 展开/收起控制 -->
        <template v-if="showExtraFields">
          <el-form-item label="地址" prop="buyeraddress">
            <el-input
              v-model="invoiceForm.buyeraddress"
              placeholder="请输入地址"
            />
          </el-form-item>

          <el-form-item label="电话" prop="buyerphone">
            <el-input
              v-model="invoiceForm.buyerphone"
              placeholder="请输入电话"
            />
          </el-form-item>

          <el-form-item label="开户行" prop="buyerbank">
            <el-input
              v-model="invoiceForm.buyerbank"
              placeholder="请输入开户行"
            />
          </el-form-item>

          <el-form-item label="银行账号" prop="buyerbankno">
            <el-input
              v-model="invoiceForm.buyerbankno"
              placeholder="请输入银行账号"
            />
          </el-form-item>
        </template>

        <!-- 展开/收起按钮 -->
        <el-form-item label=" " label-width="0">
          <div class="toggle-btn" @click="toggleExtraFields">
            <span>{{ showExtraFields ? '收起' : '展开' }}</span>
            <i :class="showExtraFields ? 'el-icon-arrow-up' : 'el-icon-arrow-down'"></i>
            <span class="toggle-desc">更多信息</span>
          </div>
        </el-form-item>

        <!-- 开票金额：只读，固化为订单金额 -->
        <el-form-item label="开票金额" prop="amount" required>
          <el-input
            :value="displayAmount"
            disabled
            placeholder="开票金额"
            style="width: 100%"
          >
            <template slot="prepend">¥</template>
          </el-input>
          <span class="ml-2 text-gray-400 text-sm">订单金额：¥{{ displayAmount }}</span>
        </el-form-item>

        <!-- 开票项目 -->
        <el-form-item label="开票项目" prop="goodsname" required>
          <el-select
            v-model="invoiceForm.goodsname"
            placeholder="请选择开票项目"
            style="width: 100%"
            @change="onGoodsChange"
          >
            <el-option
              v-for="item in goodsOptions"
              :key="item.code"
              :label="item.text"
              :value="item.text"
            />
          </el-select>
        </el-form-item>

        <!-- 备注 -->
        <el-form-item label="备注" prop="userremark">
          <el-input
            v-model="invoiceForm.userremark"
            type="textarea"
            :rows="3"
            placeholder="请输入备注"
          />
        </el-form-item>
      </el-form>
    </div>

    <div slot="footer" class="dialog-footer">
      <el-button @click="dialogVisible = false">
        {{ invoiceRecord && invoiceRecord.id ? '关闭' : '取消' }}
      </el-button>
      <el-button
        v-if="!invoiceRecord || !invoiceRecord.id"
        type="primary"
        @click="handleSubmit"
        :loading="submitting"
      >
        提交发票申请
      </el-button>
    </div>
  </el-dialog>
</template>

<script>
import { postInvoice, getMerchant, biztaxers, getInvoices } from '@/api/user'
import { getTicketList } from '@/api/event'

export default {
  name: 'InvoiceDialog',
  props: {
    visible: {
      type: Boolean,
      default: false
    },
    orderId: {
      type: String,
      required: true
    },
    orderData: {
      type: Object,
      default: () => ({})
    },
    orderAmount: {
      type: Number,
      default: 0
    },
    eventId: {
      type: [String, Number],
      default: ''
    },
    merchantId: {
      type: [String, Number],
      default: ''
    }
  },
  data() {
    return {
      dialogVisible: this.visible,
      submitting: false,
      showExtraFields: false,
      invoiceRecord: null, // 存储已有的发票记录
      invoiceForm: {
        type: '0',
        buyername: '',
        buyertaxid: '',
        buyerphone: '',
        buyeraddress: '',
        buyerbank: '',
        buyerbankno: '',
        amount: 0,
        goodscode: '',
        goodsname: '',
        recipientemail: '',
        userremark: ''
      },
      goodsOptions: [],
      taxrate: 0,
      showDropdown: false,
      searchResults: [],
      rules: {
        type: [{ required: true, message: '请选择发票类型', trigger: 'change' }],
        buyername: [{ required: true, message: '请输入单位名称', trigger: 'blur' }],
        buyertaxid: [{ required: true, message: '请输入纳税人识别号', trigger: 'blur' }],
        recipientemail: [
          { required: true, message: '请输入电子邮箱', trigger: 'blur' },
          { pattern: /^\w+([-+.]\w+)*@\w+([-.]\w+)*\.\w+([-.]\w+)*$/, message: '请输入正确的邮箱地址', trigger: 'blur' }
        ],
        amount: [{ required: true, message: '开票金额不能为空', trigger: 'blur' }],
        goodsname: [{ required: true, message: '请选择开票项目', trigger: 'change' }]
      }
    }
  },
  computed: {
    displayAmount() {
      return (this.orderAmount / 100).toFixed(2)
    },
    dialogTitle() {
      return this.invoiceRecord && this.invoiceRecord.id ? '发票信息' : '申请发票'
    }
  },
  watch: {
    visible(val) {
      this.dialogVisible = val
      if (val) {
        // 先获取发票记录
        this.loadInvoiceRecord()
      }
    },
    dialogVisible(val) {
      if (!val) {
        this.$emit('update:visible', false)
      }
    }
  },
  methods: {
    // 加载发票记录
    async loadInvoiceRecord() {
      try {
        const res = await getInvoices(this.orderId, {})
        if (res.code === 200 && res.list && res.list.length > 0) {
          this.invoiceRecord = res.list[0]
          console.log('发票记录:', this.invoiceRecord)
          // 如果有发票记录，不需要加载表单数据
          return
        }
        // 没有发票记录，加载表单
        this.invoiceRecord = null
        this.loadGoodsOptions()
        this.invoiceForm.amount = this.orderAmount / 100
        this.showExtraFields = false
      } catch (error) {
        console.error('加载发票记录失败:', error)
        this.invoiceRecord = null
        // 出错时加载表单
        this.loadGoodsOptions()
        this.invoiceForm.amount = this.orderAmount / 100
        this.showExtraFields = false
      }
    },

    // 获取状态类型
    getStatusType(status) {
      const map = {
        0: 'info',
        1: 'warning',
        2: 'success',
        3: 'danger'
      }
      return map[status] || 'info'
    },

    // 获取状态文本
    getStatusText(status) {
      const map = {
        0: '待处理',
        1: '处理中',
        2: '已完成',
        3: '已拒绝'
      }
      return map[status] || '未知状态'
    },

    // 复制PDF地址
    handleCopyPdfUrl() {
      if (!this.invoiceRecord?.pdfurl) {
        this.$message.warning('暂无发票PDF地址')
        return
      }
      navigator.clipboard?.writeText(this.invoiceRecord.pdfurl).then(() => {
        this.$message.success('PDF地址已复制')
      }).catch(() => {
        // 降级方案
        const textarea = document.createElement('textarea')
        textarea.value = this.invoiceRecord.pdfurl
        document.body.appendChild(textarea)
        textarea.select()
        document.execCommand('copy')
        document.body.removeChild(textarea)
        this.$message.success('PDF地址已复制')
      })
    },

    // 切换展开/收起
    toggleExtraFields() {
      this.showExtraFields = !this.showExtraFields
    },

    async loadGoodsOptions() {
      try {
        const t = await getTicketList(this.eventId)
        const ticket = t.list?.find(item => item.id === this.orderData?.goodids?.[0])
        if (!ticket) return

        const mResult = await getMerchant(ticket.merchantid)
        const m = mResult.data

        if (m?.taxitems && Array.isArray(m.taxitems)) {
          this.goodsOptions = m.taxitems.map(item => ({
            text: item.name,
            code: item.code,
            name: item.name.split('*')[2] || item.name
          }))
        }

        this.taxrate = this.parseTaxrate(m?.seller?.taxrate || 0)
      } catch (error) {
        console.error('加载开票项目失败:', error)
      }
    },

    parseTaxrate(rate) {
      if (typeof rate === 'number') return rate
      return parseFloat(String(rate).replace(/[^0-9.]/g, '')) / 100 || 0
    },

    onBuyernameInput() {
      if (this.invoiceForm.buyername.length > 3) {
        biztaxers({ fullname: this.invoiceForm.buyername }).then(res => {
          if (res.list?.length) {
            const item = res.list[0]
            this.invoiceForm.buyertaxid = item.taxno || ''
            this.invoiceForm.buyerbank = item.bank || ''
            this.invoiceForm.buyerbankno = item.bankno || ''
            this.invoiceForm.buyeraddress = item.regaddr || ''
            this.invoiceForm.buyerphone = item.regphone || ''
          }
        }).catch(() => {})
      }
    },

    onGoodsChange(val) {
      const selected = this.goodsOptions.find(item => item.text === val)
      if (selected) {
        this.invoiceForm.goodscode = selected.code
      }
    },

    async handleSubmit() {
      try {
        const valid = await this.$refs.invoiceFormRef.validate().catch(err => {
          console.log('表单验证失败:', err)
          return false
        })

        if (!valid) {
          return
        }

        this.submitting = true

        const params = {
          eventid: this.eventId,
          orderid: this.orderId,
          orderamount: this.orderAmount,
          type: parseInt(this.invoiceForm.type),
          amount: Number(this.invoiceForm.amount),
          taxrate: this.taxrate,
          details: [{
            goodsCode: this.invoiceForm.goodscode,
            goodsName: this.invoiceForm.goodsname,
            price: String(this.invoiceForm.amount),
            taxRate: String(this.taxrate),
            withTaxFlag: '1'
          }],
          merchantid: this.merchantId,
          buyer: {
            name: this.invoiceForm.buyername,
            taxid: this.invoiceForm.buyertaxid,
            phone: this.invoiceForm.buyerphone,
            address: this.invoiceForm.buyeraddress,
            bank: this.invoiceForm.buyerbank,
            bankno: this.invoiceForm.buyerbankno
          },
          recipient: {
            name: this.orderData?.realname || '',
            phone: this.orderData?.cellphone || '',
            email: this.invoiceForm.recipientemail
          },
          userremark: this.invoiceForm.userremark
        }

        const res = await postInvoice(this.orderId, params)

        if (res.code === 201) {
          this.$message.success('发票申请已提交，请等待处理')
          this.dialogVisible = false
          this.$emit('success', res.data)
        } else {
          this.$message.error(res.err || '发票申请失败，请稍后重试')
        }
      } catch (error) {
        console.error('提交发票申请失败:', error)
        this.$message.error('发票申请失败，请稍后重试')
      } finally {
        this.submitting = false
      }
    },

    handleClose() {
      this.dialogVisible = false
      this.$emit('update:visible', false)
    },

    resetForm() {
      this.invoiceForm = {
        type: '0',
        buyername: '',
        buyertaxid: '',
        buyerphone: '',
        buyeraddress: '',
        buyerbank: '',
        buyerbankno: '',
        amount: this.orderAmount / 100,
        goodscode: '',
        goodsname: '',
        recipientemail: '',
        userremark: ''
      }
      this.showExtraFields = false
      this.invoiceRecord = null
      this.$refs.invoiceFormRef?.clearValidate()
    }
  }
}
</script>

<style scoped>
.invoice-dialog {
  padding: 10px 0;
}
.invoice-dialog .el-form-item {
  margin-bottom: 18px;
}
.invoice-dialog .el-input-number {
  width: 100%;
}
.ml-2 {
  margin-left: 8px;
}
.text-gray-400 {
  color: #9ca3af;
}
.text-sm {
  font-size: 12px;
}
.dialog-footer {
  display: flex;
  justify-content: flex-end;
  gap: 10px;
}

/* 展开/收起按钮样式 */
.toggle-btn {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  color: #409eff;
  cursor: pointer;
  font-size: 14px;
  user-select: none;
  padding: 4px 0;
  transition: color 0.2s;
}
.toggle-btn:hover {
  color: #66b1ff;
}
.toggle-btn .toggle-desc {
  color: #909399;
  font-size: 12px;
  margin-left: 4px;
}
.toggle-btn i {
  font-size: 14px;
  transition: transform 0.3s ease;
}

/* 发票详情样式 */
.invoice-detail {
  padding: 10px 0;
}
.detail-item {
  display: flex;
  align-items: flex-start;
  padding: 8px 0;
  border-bottom: 1px solid #f0f0f0;
}
.detail-item:last-child {
  border-bottom: none;
}
.detail-label {
  width: 120px;
  flex-shrink: 0;
  color: #909399;
  font-size: 14px;
}
.detail-value {
  flex: 1;
  color: #303133;
  font-size: 14px;
  word-break: break-all;
}

/* 禁用状态样式 */
:deep(.el-input.is-disabled .el-input__inner) {
  background-color: #f5f7fa;
  color: #606266;
  cursor: not-allowed;
}
:deep(.el-input-group__prepend) {
  background-color: #f5f7fa;
  color: #606266;
}
</style>